import { useCallback, useEffect, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import MuiLink from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tab from '@mui/material/Tab';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tabs from '@mui/material/Tabs';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';

import * as legalApi from '../../api/legal';
import type { LegalAdminDocument, LegalKind } from '../../api/legal';
import { LEGAL_KIND_LABEL, LEGAL_KIND_PATH } from '../../api/legal';
import { extractErrorMessage } from '../../api/errors';
import { LegalMarkdown } from '../../components/legal/LegalMarkdown';

const KINDS: LegalKind[] = ['terms', 'privacy'];

function statusOf(d: LegalAdminDocument, now: number): 'Draft' | 'Upcoming' | 'Published' {
  if (!d.published_at) return 'Draft';
  if (d.effective_at && new Date(d.effective_at).getTime() > now) return 'Upcoming';
  return 'Published';
}

function fmt(iso: string | null): string {
  return iso ? new Date(iso).toLocaleString() : '—';
}

/**
 * Terms of Use and Privacy Policy (LEGAL-TERMS), superadmin only: write a
 * draft (Markdown, with a preview), then publish it. A published version is
 * a record and can't be edited — the next change is a new draft, which
 * starts from the latest text. "Material" asks every signed-in user to
 * accept again; a later effective date shows the new version as upcoming
 * until then.
 */
export function LegalDocuments({ embedded = false }: { embedded?: boolean } = {}) {
  const [docs, setDocs] = useState<LegalAdminDocument[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<LegalAdminDocument | null>(null);
  const [publishing, setPublishing] = useState<LegalAdminDocument | null>(null);

  const load = useCallback(async () => {
    try {
      setDocs(await legalApi.listLegalDocuments());
    } catch (err) {
      setError(extractErrorMessage(err, 'could not load the documents'));
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const newDraft = async (kind: LegalKind) => {
    setError(null);
    const latest = docs?.find((d) => d.kind === kind);
    try {
      const draft = await legalApi.createLegalDraft({
        kind,
        title: latest?.title ?? LEGAL_KIND_LABEL[kind],
        body_md: latest?.body_md ?? `# ${LEGAL_KIND_LABEL[kind]}\n\n`,
      });
      await load();
      setEditing(draft);
    } catch (err) {
      setError(extractErrorMessage(err, 'could not create the draft'));
    }
  };

  const removeDraft = async (d: LegalAdminDocument) => {
    if (!window.confirm(`Delete the draft of ${LEGAL_KIND_LABEL[d.kind]} v${d.version}?`)) {
      return;
    }
    try {
      await legalApi.deleteLegalDraft(d.id);
      await load();
    } catch (err) {
      setError(extractErrorMessage(err, 'could not delete the draft'));
    }
  };

  if (docs === null && !error) {
    return (
      <Stack alignItems="center" sx={{ py: 4 }}>
        <CircularProgress />
      </Stack>
    );
  }

  const now = Date.now();
  return (
    <Stack spacing={3}>
      {!embedded && <Typography variant="h4">Terms and privacy</Typography>}
      <Typography variant="body2" color="text.secondary">
        Anyone joining by email (sign-up or invitation) must tick that they
        accept the current versions. Until a version is published nothing is
        asked. Publishing as <strong>material</strong> also asks every
        signed-in user to accept again.
      </Typography>
      {error && <Alert severity="error">{error}</Alert>}
      {KINDS.map((kind) => {
        const rows = (docs ?? []).filter((d) => d.kind === kind);
        const hasDraft = rows.some((d) => !d.published_at);
        return (
          <Card key={kind} variant="outlined">
            <CardContent>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Stack direction="row" spacing={1.5} alignItems="baseline">
                  <Typography variant="h6">{LEGAL_KIND_LABEL[kind]}</Typography>
                  <MuiLink href={LEGAL_KIND_PATH[kind]} target="_blank" rel="noopener">
                    Public page
                  </MuiLink>
                </Stack>
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => void newDraft(kind)}
                  disabled={hasDraft}
                  title={hasDraft ? 'Finish or delete the open draft first' : undefined}
                >
                  New version
                </Button>
              </Stack>
              {rows.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No versions yet.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Version</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Effective</TableCell>
                      <TableCell align="right">Accepted by</TableCell>
                      <TableCell />
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((d) => {
                      const st = statusOf(d, now);
                      return (
                        <TableRow key={d.id}>
                          <TableCell>v{d.version}</TableCell>
                          <TableCell>{d.title}</TableCell>
                          <TableCell>
                            <Stack direction="row" spacing={0.5}>
                              <Chip
                                size="small"
                                label={st}
                                variant={st === 'Published' ? 'filled' : 'outlined'}
                              />
                              {d.published_at && d.material && (
                                <Chip size="small" variant="outlined" label="Material" />
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell>{fmt(d.effective_at)}</TableCell>
                          <TableCell align="right">{d.acceptance_count}</TableCell>
                          <TableCell align="right">
                            {d.published_at ? (
                              <Button size="small" onClick={() => setEditing(d)}>
                                View
                              </Button>
                            ) : (
                              <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                                <Button size="small" onClick={() => setEditing(d)}>
                                  Edit
                                </Button>
                                <Button size="small" onClick={() => setPublishing(d)}>
                                  Publish
                                </Button>
                                <Button
                                  size="small"
                                  color="error"
                                  onClick={() => void removeDraft(d)}
                                >
                                  Delete
                                </Button>
                              </Stack>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        );
      })}
      {editing && (
        <DraftEditor
          doc={editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await load();
          }}
        />
      )}
      {publishing && (
        <PublishDialog
          doc={publishing}
          isFirst={!(docs ?? []).some((d) => d.kind === publishing.kind && d.published_at)}
          onClose={() => setPublishing(null)}
          onPublished={async () => {
            setPublishing(null);
            await load();
          }}
        />
      )}
    </Stack>
  );
}

function DraftEditor({
  doc,
  onClose,
  onSaved,
}: {
  doc: LegalAdminDocument;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const readOnly = doc.published_at !== null;
  const [title, setTitle] = useState(doc.title);
  const [body, setBody] = useState(doc.body_md);
  const [tab, setTab] = useState(readOnly ? 1 : 0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await legalApi.updateLegalDraft(doc.id, { title, body_md: body });
      await onSaved();
    } catch (err) {
      setError(extractErrorMessage(err, 'could not save the draft'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {LEGAL_KIND_LABEL[doc.kind]} v{doc.version}
        {readOnly ? ' (published)' : ' (draft)'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            disabled={readOnly || saving}
            fullWidth
          />
          <Tabs value={tab} onChange={(_e, v: number) => setTab(v)}>
            <Tab label="Markdown" disabled={readOnly} />
            <Tab label="Preview" />
          </Tabs>
          {tab === 0 ? (
            <TextField
              label="Text (Markdown)"
              value={body}
              onChange={(e) => setBody(e.target.value)}
              disabled={saving}
              multiline
              minRows={18}
              fullWidth
              slotProps={{ htmlInput: { style: { fontFamily: 'monospace', fontSize: 13 } } }}
            />
          ) : (
            <Box sx={{ maxHeight: '60vh', overflowY: 'auto', pr: 1 }}>
              <LegalMarkdown source={body} />
            </Box>
          )}
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{readOnly ? 'Close' : 'Cancel'}</Button>
        {!readOnly && (
          <Button
            variant="contained"
            onClick={() => void save()}
            disabled={saving || !title.trim() || !body.trim()}
          >
            Save draft
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

function PublishDialog({
  doc,
  isFirst,
  onClose,
  onPublished,
}: {
  doc: LegalAdminDocument;
  isFirst: boolean;
  onClose: () => void;
  onPublished: () => Promise<void>;
}) {
  const [material, setMaterial] = useState(true);
  const [effective, setEffective] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const publish = async () => {
    setSaving(true);
    setError(null);
    try {
      await legalApi.publishLegalDocument(doc.id, {
        material,
        effective_at: effective ? new Date(effective).toISOString() : null,
      });
      await onPublished();
    } catch (err) {
      setError(extractErrorMessage(err, 'could not publish'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Publish {LEGAL_KIND_LABEL[doc.kind]} v{doc.version}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            A published version can't be edited or deleted. From its effective
            date, everyone joining must accept it.
          </Typography>
          {isFirst ? (
            <Alert severity="info">
              This is the first {LEGAL_KIND_LABEL[doc.kind]}: every signed-in
              user will be asked to accept it, material or not.
            </Alert>
          ) : (
            <FormControlLabel
              control={
                <Switch checked={material} onChange={(e) => setMaterial(e.target.checked)} />
              }
              label="Material change: ask every signed-in user to accept again"
            />
          )}
          <TextField
            label="Effective from (optional)"
            type="datetime-local"
            value={effective}
            onChange={(e) => setEffective(e.target.value)}
            helperText="Leave empty to take effect now. A later date shows it as upcoming until then."
            slotProps={{ inputLabel: { shrink: true } }}
          />
          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={() => void publish()} disabled={saving}>
          Publish
        </Button>
      </DialogActions>
    </Dialog>
  );
}
